/**
 * Train — the v2 active-session screen.
 *
 * Implements the Train screen of AppScreen.dc.html ("OnFlow Design System")
 * on top of the existing active-workout store, so behaviour is unchanged:
 * stopwatch resumes from startedAt on refresh, set types cycle
 * normal → warmup → dropset → failure, completing a set starts the rest
 * timer, Finish POSTs to /workouts, Discard clears without saving.
 *
 * What the template adds:
 *   - elapsed + volume as one ember-ringed card with Finish / Discard inline
 *   - set rows as a grid (SET / PREV / KG / REPS / ✓) with recessed inputs
 *     and an on-track fill on the completed row
 *   - the rest timer as a full-screen countdown ring rather than a modal box
 *   - a week Scorecard under the session, from recent workouts
 *
 * Deviation: the add-exercise picker stays the existing ExercisePicker
 * component — its two-level muscle taxonomy is richer than the template's
 * four chips and there is no reason to lose it.
 */
import type { CreateWorkoutInput, Exercise, Units } from '@onflow/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { ExercisePicker } from '../components/ExercisePicker.js';
import { ConfirmDialog } from '../components/v2/ConfirmDialog.js';
import { FlowButton } from '../components/v2/FlowButton.js';
import { FlowSegment } from '../components/v2/FlowInput.js';
import { Icon } from '../components/v2/Icon.js';
import { Scorecard } from '../components/v2/Scorecard.js';
import { useUpdateProfile } from '../hooks/useAuth.js';
import { useExercises } from '../hooks/useExercises.js';
import { useRecentWorkouts } from '../hooks/useStats.js';
import { useCreateWorkout } from '../hooks/useWorkouts.js';
import { kgToUnit, unitLabel, unitToKg } from '../lib/format.js';
import { toast } from '../lib/toast.js';
import { weekScorecard } from '../lib/track.js';
import { useActiveWorkoutStore } from '../stores/activeWorkoutStore.js';
import { useAuthStore } from '../stores/authStore.js';

const EYEBROW = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase' as const,
  color: 'var(--ink-3)',
};

const CARD = {
  background: 'var(--paper-1)',
  border: '1px solid var(--edge)',
  borderRadius: 'var(--r-md)',
  boxShadow: 'var(--elev-1)',
};

const GRID = '26px 1fr 54px 54px 36px 28px';

const CELL_INPUT = {
  background: 'var(--paper-0)',
  border: '1px solid var(--edge)',
  borderRadius: 'var(--r-xs)',
  padding: '8px 0',
  width: '100%',
  textAlign: 'center' as const,
  fontFamily: 'var(--font-display)',
  fontSize: 15,
  fontWeight: 700,
  color: 'var(--ink-1)',
  fontVariantNumeric: 'tabular-nums' as const,
  boxShadow: 'inset 0 1px 3px rgba(0,0,0,.4)',
};

function formatHMS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

// Single high-pitched beep when the rest timer hits zero. WebAudio is the
// path of least resistance — no asset to ship and no permission prompt.
function playBeep() {
  try {
    const ctx = new (
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    )();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start();
    osc.stop(ctx.currentTime + 0.45);
  } catch {
    // Some browsers block AudioContext until first user interaction; no beep
    // is preferable to a thrown exception.
  }
}

export function WorkoutSession() {
  const navigate = useNavigate();
  const workout = useActiveWorkoutStore((s) => s.workout);
  const setTitle = useActiveWorkoutStore((s) => s.setTitle);
  const addExercise = useActiveWorkoutStore((s) => s.addExercise);
  const removeExercise = useActiveWorkoutStore((s) => s.removeExercise);
  const addSet = useActiveWorkoutStore((s) => s.addSet);
  const removeSet = useActiveWorkoutStore((s) => s.removeSet);
  const updateSet = useActiveWorkoutStore((s) => s.updateSet);
  const cycleSetType = useActiveWorkoutStore((s) => s.cycleSetType);
  const toggleSetCompleted = useActiveWorkoutStore((s) => s.toggleSetCompleted);
  const tickRestTimer = useActiveWorkoutStore((s) => s.tickRestTimer);
  const skipRestTimer = useActiveWorkoutStore((s) => s.skipRestTimer);
  const discard = useActiveWorkoutStore((s) => s.discard);

  const profile = useAuthStore((s) => s.profile);
  const defaultRest = profile?.defaultRestTimer ?? 90;
  const updateProfile = useUpdateProfile();
  const units: Units = profile?.units ?? 'KG';

  const exercises = useExercises();
  const createWorkout = useCreateWorkout();
  const recent = useRecentWorkouts(12);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirm, setConfirm] = useState<null | 'discard' | 'finish'>(null);
  const [now, setNow] = useState(() => Date.now());

  // Set true the instant Finish starts clearing the store, so the guard effect
  // below doesn't race it — otherwise `discard()` nulls the workout, the guard
  // fires first and redirects to /workout/new, clobbering the /dashboard nav.
  const finishingRef = useRef(false);

  // Landing here without an in-progress workout (refresh after Finish, or a
  // direct URL hit) bounces back to the start screen — but not while Finish is
  // intentionally sending the user to the dashboard.
  useEffect(() => {
    if (!workout && !finishingRef.current) navigate('/workout/new', { replace: true });
  }, [workout, navigate]);

  // 1Hz tick drives both the stopwatch and the rest-timer countdown.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!workout?.rest) return;
    if (workout.rest.remainingSeconds <= 1) {
      // `tickRestTimer` zeroes it out; beep on the transition.
      playBeep();
    }
    tickRestTimer();
    // The dependency on `now` is intentional — `tickRestTimer` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now]);

  const exerciseById = useMemo(() => {
    const map = new Map<string, Exercise>();
    for (const ex of exercises.data ?? []) map.set(ex.id, ex);
    return map;
  }, [exercises.data]);

  // PREV column data: the same exercise's sets from the most recent saved
  // workout that included it, so each row shows last session's load to beat.
  // Recent workouts come newest-first, so the first occurrence wins.
  const prevByExercise = useMemo(() => {
    const map = new Map<string, { weight: number; reps: number }[]>();
    for (const w of recent.data?.items ?? []) {
      for (const ex of w.exercises ?? []) {
        if (!map.has(ex.exerciseId)) {
          map.set(
            ex.exerciseId,
            ex.sets.map((s) => ({ weight: s.weight, reps: s.reps })),
          );
        }
      }
    }
    return map;
  }, [recent.data]);

  if (!workout) return null;

  const elapsedSeconds = Math.max(
    0,
    Math.floor((now - new Date(workout.startedAt).getTime()) / 1000),
  );

  const allSets = workout.exercises.flatMap((e) => e.sets);
  const doneSets = allSets.filter((s) => s.completed);
  const volume = doneSets.reduce((a, s) => a + s.weight * s.reps, 0);

  // Trim incomplete sets so the saved session reflects what was actually
  // done — it keeps post-workout analytics honest. Exercises with no completed
  // sets drop entirely, then surviving sets renumber contiguously.
  const buildCleaned = () =>
    workout.exercises
      .map((ex) => ({ ...ex, sets: ex.sets.filter((s) => s.completed) }))
      .filter((ex) => ex.sets.length > 0)
      .map((ex) => ({ ...ex, sets: ex.sets.map((s, i) => ({ ...s, setNumber: i + 1 })) }));

  const doFinish = async () => {
    setConfirm(null);
    const cleaned = buildCleaned();
    if (cleaned.length === 0) {
      toast.error('Mark at least one set complete before finishing.');
      return;
    }

    const payload: CreateWorkoutInput = {
      title: workout.title.trim() || 'Workout',
      date: workout.startedAt,
      duration: elapsedSeconds,
      notes: workout.notes ? workout.notes : null,
      exercises: cleaned,
      // Plan linkage is optional and nullable — both fields must travel
      // together (the DTO refine enforces it), so only forward them when both
      // came from the store via the dashboard's "Log Workout" handler.
      ...(workout.planId && workout.planSessionId
        ? { planId: workout.planId, planSessionId: workout.planSessionId }
        : {}),
    };

    try {
      await createWorkout.mutateAsync(payload);
      finishingRef.current = true;
      toast.success('Workout saved');
      discard();
      navigate('/dashboard');
    } catch (err) {
      // Surface why so the user isn't stuck. The active workout is preserved
      // so they can retry.
      toast.error(err instanceof Error ? err.message : 'Save failed');
    }
  };

  const handleFinish = () => {
    const cleaned = buildCleaned();
    if (cleaned.length === 0) {
      toast.error('Mark at least one set complete before finishing.');
      return;
    }
    // Warn before silently dropping sets that carry data but were never
    // marked complete — that's real logged work about to vanish.
    const hasUnsavedData = workout.exercises.some((ex) =>
      ex.sets.some((s) => !s.completed && (s.weight > 0 || s.reps > 0)),
    );
    if (hasUnsavedData) {
      setConfirm('finish');
      return;
    }
    void doFinish();
  };

  const handleDiscard = () => setConfirm('discard');

  const handlePickExercise = (ex: Exercise) => {
    addExercise(ex.id, defaultRest);
    setPickerOpen(false);
  };

  const week = weekScorecard(recent.data?.items ?? [], new Date());
  const rest = workout.rest;

  return (
    <section className="fade-in">
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 12,
          padding: '6px 0 14px',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <span style={{ ...EYEBROW, display: 'block', marginBottom: 6 }}>Session in progress</span>
          <input
            type="text"
            value={workout.title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Workout title"
            maxLength={200}
            aria-label="Workout title"
            style={{
              width: '100%',
              margin: 0,
              padding: 0,
              background: 'transparent',
              border: 0,
              outline: 'none',
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-title-size)',
              lineHeight: 'var(--text-title-line)',
              letterSpacing: 'var(--text-title-tracking)',
              fontWeight: 700,
              color: 'var(--ink-1)',
            }}
          />
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 5,
            padding: '6px 11px',
            borderRadius: 'var(--r-full)',
            background: 'var(--paper-2)',
            border: '1px solid var(--edge)',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 14,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {doneSets.length}/{allSets.length}
          </span>
          <span style={{ ...EYEBROW, fontSize: 10, letterSpacing: '0.08em' }}>sets</span>
        </span>
      </header>

      <div
        style={{
          ...CARD,
          border: '1px solid color-mix(in srgb, var(--ember) 40%, transparent)',
          boxShadow: 'var(--elev-1), var(--ring-ember)',
          padding: 18,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div>
            <span style={{ ...EYEBROW, fontSize: 11, display: 'block' }}>Elapsed</span>
            <div
              style={{
                marginTop: 6,
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-metric-size)',
                lineHeight: 1,
                letterSpacing: 'var(--text-metric-tracking)',
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatHMS(elapsedSeconds)}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ ...EYEBROW, fontSize: 11, display: 'block' }}>Volume</span>
            <div
              style={{
                marginTop: 8,
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-metric-sm-size)',
                lineHeight: 1,
                letterSpacing: '-0.03em',
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {Math.round(kgToUnit(volume, units)).toLocaleString()}
              <span
                style={{
                  color: 'var(--ink-3)',
                  fontWeight: 600,
                  fontSize: '0.42em',
                  marginLeft: 6,
                }}
              >
                {unitLabel(units)}
              </span>
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 14,
          }}
        >
          <span style={{ ...EYEBROW, fontSize: 10 }}>Weight unit</span>
          <FlowSegment
            aria-label="Weight unit"
            items={[
              { id: 'KG', label: 'kg' },
              { id: 'LB', label: 'lb' },
            ]}
            active={units}
            onSelect={(id) => updateProfile.mutate({ units: id as Units })}
            style={{ width: 104 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <FlowButton
            variant="primary"
            block
            disabled={createWorkout.isPending}
            onClick={handleFinish}
          >
            {createWorkout.isPending ? 'Saving…' : 'Finish session'}
          </FlowButton>
          <FlowButton variant="danger" disabled={createWorkout.isPending} onClick={handleDiscard}>
            Discard
          </FlowButton>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          margin: '22px 0 10px',
        }}
      >
        <span style={EYEBROW}>Exercises</span>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          style={{
            ...EYEBROW,
            background: 'none',
            border: 0,
            padding: 0,
            cursor: 'pointer',
            color: 'var(--ember)',
          }}
        >
          Add
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {workout.exercises.map((exercise, exerciseIdx) => {
          const meta = exerciseById.get(exercise.exerciseId);
          return (
            <div key={`${exercise.exerciseId}-${exerciseIdx}`} style={{ ...CARD, padding: 14 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  padding: '2px 4px 10px',
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 15,
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {meta?.name ?? 'Unknown exercise'}
                </span>
                <button
                  type="button"
                  onClick={() => removeExercise(exerciseIdx)}
                  aria-label="Remove exercise"
                  title="Remove exercise"
                  style={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: 'var(--r-full)',
                    background: 'transparent',
                    border: '1px solid var(--edge)',
                    color: 'var(--ink-3)',
                    cursor: 'pointer',
                  }}
                >
                  <Icon name="close" size={14} />
                </button>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: GRID,
                  gap: 8,
                  padding: '0 12px 6px',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 9,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-3)',
                }}
              >
                <span>Set</span>
                <span>Prev</span>
                <span style={{ textAlign: 'center' }}>{unitLabel(units)}</span>
                <span style={{ textAlign: 'center' }}>Reps</span>
                <span />
                <span />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {exercise.sets.map((set, setIdx) => {
                  const prevSet = prevByExercise.get(exercise.exerciseId)?.[setIdx];
                  const prevLabel =
                    prevSet && (prevSet.weight > 0 || prevSet.reps > 0)
                      ? `${kgToUnit(prevSet.weight, units)}×${prevSet.reps}`
                      : '-';
                  const badge =
                    set.type === 'warmup'
                      ? 'W'
                      : set.type === 'dropset'
                        ? 'D'
                        : set.type === 'failure'
                          ? 'F'
                          : String(setIdx + 1);
                  const badgeInk =
                    set.type === 'warmup'
                      ? 'var(--drifting)'
                      : set.type === 'dropset'
                        ? 'var(--macro-fat)'
                        : set.type === 'failure'
                          ? 'var(--off-track)'
                          : 'var(--ink-2)';
                  return (
                    <div
                      key={setIdx}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: GRID,
                        gap: 8,
                        alignItems: 'center',
                        padding: '7px 12px',
                        borderRadius: 'var(--r-sm)',
                        background: set.completed ? 'var(--on-track-wash)' : 'transparent',
                        transition: 'background var(--t-state) var(--ease-out)',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => cycleSetType(exerciseIdx, setIdx)}
                        title="Tap to change set type (normal → warmup → dropset → failure)"
                        aria-label="Change set type"
                        style={{
                          justifySelf: 'start',
                          minWidth: 24,
                          height: 24,
                          padding: '0 4px',
                          display: 'grid',
                          placeItems: 'center',
                          background: 'var(--paper-2)',
                          border: '1px solid var(--edge)',
                          borderRadius: 'var(--r-xs)',
                          cursor: 'pointer',
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11,
                          fontWeight: 700,
                          color: badgeInk,
                        }}
                      >
                        {badge}
                      </button>
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11,
                          color: 'var(--ink-2)',
                        }}
                      >
                        {prevLabel}
                      </span>
                      <input
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step={0.5}
                        value={set.weight === 0 ? '' : kgToUnit(set.weight, units)}
                        placeholder="0"
                        aria-label={`Set ${setIdx + 1} weight`}
                        onChange={(e) =>
                          updateSet(exerciseIdx, setIdx, {
                            weight: unitToKg(Number(e.target.value) || 0, units),
                          })
                        }
                        style={CELL_INPUT}
                      />
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        step={1}
                        value={set.reps === 0 ? '' : set.reps}
                        placeholder="0"
                        aria-label={`Set ${setIdx + 1} reps`}
                        onChange={(e) =>
                          updateSet(exerciseIdx, setIdx, { reps: Number(e.target.value) || 0 })
                        }
                        style={CELL_INPUT}
                      />
                      <button
                        type="button"
                        onClick={() => toggleSetCompleted(exerciseIdx, setIdx)}
                        aria-label={set.completed ? 'Unmark set complete' : 'Mark set complete'}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 'var(--r-xs)',
                          cursor: 'pointer',
                          display: 'grid',
                          placeItems: 'center',
                          justifySelf: 'end',
                          background: set.completed ? 'var(--on-track)' : 'var(--paper-3)',
                          border: `1px solid ${set.completed ? 'var(--on-track)' : 'var(--edge)'}`,
                          color: set.completed ? 'var(--paper-0)' : 'var(--ink-3)',
                          boxShadow: set.completed
                            ? '0 0 18px color-mix(in srgb, var(--on-track) 35%, transparent)'
                            : 'var(--elev-1)',
                          animation: set.completed ? 'v2-pop 260ms var(--ease-snap)' : undefined,
                          transition:
                            'background var(--t-tap) var(--ease-out), color var(--t-tap) var(--ease-out)',
                        }}
                      >
                        <Icon name="check" size={18} strokeWidth={2.6} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeSet(exerciseIdx, setIdx)}
                        aria-label={`Remove set ${setIdx + 1}`}
                        title="Remove set"
                        style={{
                          justifySelf: 'center',
                          width: 26,
                          height: 26,
                          display: 'grid',
                          placeItems: 'center',
                          background: 'transparent',
                          border: 0,
                          borderRadius: 'var(--r-full)',
                          color: 'var(--ink-3)',
                          cursor: 'pointer',
                        }}
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: 10 }}>
                <FlowButton variant="quiet" size="sm" block onClick={() => addSet(exerciseIdx)}>
                  Add set
                </FlowButton>
              </div>
            </div>
          );
        })}
      </div>

      {workout.exercises.length === 0 && (
        <div style={{ ...CARD, padding: 18 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-2)' }}>
            No exercises yet. Add the first one to start logging sets.
          </p>
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <FlowButton variant="soft" block onClick={() => setPickerOpen(true)}>
          Add exercise
        </FlowButton>
      </div>

      <div style={{ ...EYEBROW, margin: '22px 0 10px' }}>This week</div>
      <div style={{ ...CARD, padding: 18, marginBottom: 24 }}>
        <Scorecard days={week.days} rows={[{ label: 'Sessions', marks: week.marks }]} />
        <p
          style={{
            margin: '14px 0 0',
            fontSize: 'var(--text-body-sm-size)',
            lineHeight: 'var(--text-body-sm-line)',
            color: 'var(--ink-2)',
          }}
        >
          {week.sentence}
        </p>
      </div>

      <ExercisePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handlePickExercise}
        excludeIds={workout.exercises.map((e) => e.exerciseId)}
      />

      <ConfirmDialog
        open={confirm === 'discard'}
        title="Discard workout?"
        message="All entered sets will be lost. This can't be undone."
        confirmLabel="Discard"
        cancelLabel="Keep going"
        confirmVariant="danger"
        onConfirm={() => {
          setConfirm(null);
          discard();
          navigate('/workout/new', { replace: true });
        }}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={confirm === 'finish'}
        title="Finish with unsaved sets?"
        message="Some sets have weight or reps but aren't marked complete. Only completed sets are saved. The rest will be dropped."
        confirmLabel="Finish anyway"
        cancelLabel="Back"
        onConfirm={() => void doFinish()}
        onCancel={() => setConfirm(null)}
      />

      {rest &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="rest-timer-title"
            style={{
              position: 'fixed',
              inset: 0,
              // Portaled to body and above the fixed nav (z-index 100) so the
              // tab bar doesn't poke through the full-screen countdown.
              zIndex: 1000,
              background: 'color-mix(in srgb, var(--paper-0) 92%, transparent)',
              backdropFilter: 'blur(10px)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <div style={{ textAlign: 'center', animation: 'v2-rise 300ms var(--ease-out) both' }}>
              <div style={{ position: 'relative', width: 200, height: 200, margin: '0 auto' }}>
                <svg
                  width="200"
                  height="200"
                  viewBox="0 0 200 200"
                  style={{ transform: 'rotate(-90deg)' }}
                  aria-hidden
                >
                  <circle
                    cx="100"
                    cy="100"
                    r="86"
                    fill="none"
                    stroke="var(--paper-2)"
                    strokeWidth="10"
                  />
                  <circle
                    cx="100"
                    cy="100"
                    r="86"
                    fill="none"
                    stroke="var(--ember)"
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 86}
                    strokeDashoffset={
                      2 *
                      Math.PI *
                      86 *
                      (1 - Math.max(0, rest.remainingSeconds) / Math.max(1, rest.targetSeconds))
                    }
                    style={{
                      transition: 'stroke-dashoffset 1s linear',
                      filter:
                        'drop-shadow(0 0 12px color-mix(in srgb, var(--ember) 50%, transparent))',
                    }}
                  />
                </svg>
                <div
                  style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <span
                      id="rest-timer-title"
                      style={{
                        ...EYEBROW,
                        display: 'block',
                        letterSpacing: '0.14em',
                        marginBottom: 8,
                      }}
                    >
                      Rest
                    </span>
                    <span
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 56,
                        fontWeight: 700,
                        letterSpacing: '-0.04em',
                        lineHeight: 1,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {Math.max(0, rest.remainingSeconds)}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 11,
                        color: 'var(--ink-3)',
                        marginTop: 6,
                      }}
                    >
                      seconds
                    </span>
                  </div>
                </div>
              </div>
              <p
                style={{
                  margin: '20px auto 0',
                  maxWidth: 230,
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'var(--ink-2)',
                }}
              >
                {doneSets.length} set{doneSets.length === 1 ? '' : 's'} banked ·{' '}
                {Math.round(volume).toLocaleString()} kg volume.
              </p>
              <div style={{ marginTop: 18, display: 'flex', justifyContent: 'center' }}>
                <FlowButton variant="soft" onClick={skipRestTimer}>
                  Skip rest
                </FlowButton>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
}
